// @ts-nocheck
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { rm_double } from "includes/arrays";
import genRequest from "includes/request";
import { useQueryClient } from "react-query";
import Storage from "classes/Storage/Storage";
import clearSession from "classes/Accounts/clearSession";
import { sameAccount } from "classes/Accounts/hooks/useAccounts";
import formatDate from "includes/formatDate";
import { JSONDiff } from "includes/JSON";
import * as Sentry from "@sentry/react";

/**
 * @class
 * @example
 * const { account } = useAccount();
 * return (
 * 	<div>{ account.firstName }</div>
 * );
 */
function useAccount() {
	const [account, setAccount] = useState(getCurAccount());
	const queryClient = useQueryClient();
	const storage = new Storage();
	const sess_storage = new Storage(null, null, localStorage);

	const getAccounts = () => {
		const accounts_string = storage.getItem("Accounts");
		let accounts = [];
		try {
			accounts = accounts_string;
		} catch (e) {
			accounts = [];
		}
		return accounts ? accounts : [];
	};

	const getLocalAccount = (login) => {
		let accounts = getAccounts();
		let cur_account = getCurAccount();
		let ret = accounts.find(
			(a) => (login && a.login === login) || sameAccount(a, cur_account)
		);
		return ret ? ret : {};
	};

	const unread = (type, data) => {
		if (!type) return false;
		let session_account = getCurAccount();
		if (typeof data !== "undefined") {
			let updated = { ...session_account, ["unreaded_" + type]: data };
			setCurAccount(updated);
			return updated["unreaded_" + type];
		}
		return session_account["unreaded_" + type];
	};

	/**
	 * Save current account un sessionStorage
	 * @param {object:JSON} userAccount
	 */
	const setCurAccount = (userAccount) => {
		let values = { ...account, ...userAccount };
		sess_storage.setItem("account", values);
		Sentry.setUser(getMinimalInfos(values));
		setAccount(values);
	};

	const getMinimalInfos = (user) => {
		if (!user) return null;
		let ret = {
			id: user.employeeId,
			companyId: user.companyId,
			siteId: user.siteId,
			aspNetUserId: user.aspNetUserId,
			userTemplateId: user.userTemplateId,
			workFamilyId: user.workFamilyId,
			workFunctionId: user.workFunctionId,
			firstConnexion: user.firstConnexion,
			lastConnexion: user.lastConnexion,
			role: user.role,
			tokenRegeneration: user.tokenRegeneration,
			bypassModeration: user.bypassModeration,
			isActive: user.isActive,
			clientVersion: import.meta.env.VITE_VERSION,
		};
		if (user.tempValues) ret.tempUser = getMinimalInfos(user.tempValues);
		return ret;
	};

	const saveInLocal = (userAccount) => {
		let accounts = getAccounts();

		if (accounts && accounts.length) {
			const idx = accounts.findIndex((a) => sameAccount(a, userAccount));
			if (idx !== -1) {
				accounts[idx] = userAccount;
			}
		}
		else accounts.push(userAccount);
		storage.setItem("Accounts", accounts);
	};

	/**
	 * Add posts to unreadeds list
	 * @param {PostModel[]} posts
	 * @return {unreadeds[]}
	 */
	const setNewPosts = (posts) => {
		let local_account = getLocalAccount();
		let unreadeds = getUnreadedPosts();

		for (let post of posts)
			if (
				post.employeeId !== local_account.employeeId &&
				post.draft === false &&
				post.postTypeId !== 8 // don"t save events
			)
				unreadeds = addNewPost(unreadeds, post);
		local_account.unreadeds = unreadeds;
		saveInLocal(local_account);
		if (local_account) setCurAccount(local_account, true);
		return unreadeds;
	};

	const addNewPost = (unreadedsList, post) => {
		let unreadeds = unreadedsList;

		if (!unreadeds || !unreadeds.length) unreadeds = [];

		// Check readeds list directly — isReaded() can't distinguish "read" from
		// "never seen" when we're building the unreadeds list
		let readeds = getReadedsPosts();
		let alreadyRead = readeds.some((r) => r.postId === post.postId);
		let alreadyUnread = unreadeds.some((u) => u.postId === post.postId);

		if (!alreadyRead && !alreadyUnread)
			unreadeds.push({
				postId: post.postId,
				postTypeId: post.postTypeId,
			});
		return unreadeds;
	};

	/**
	 * Return array of unreaded posts datas from account saved in localStorage
	 * @param {int} typeId get only unreaded posts for a specific PostTypeId
	 * @return {unreadeds[]}
	 * @example
	 * const { getUnreadedPosts } = useAccount();
	 *
	 * const unreadeds = getUnreadedPosts();
	 * // return example
	 * [{
	 *   postId: 19856,
	 *   postTypeId: 1
	 *  },
	 *  {
	 *   postId: 20420,
	 *   postTypeId: 4
	 *  },
	 *  ...
	 * ]
	 */
	const getUnreadedPosts = (typeId) => {
		let local_account = getLocalAccount();
		let cur_account = getCurAccount();
		let readeds = getReadedsPosts();
		let unreadeds = [];

		if (!local_account.unreadeds) local_account.unreadeds = [];
		if (!cur_account.unreadeds) cur_account.unreadeds = [];
		unreadeds = unreadeds.concat(
			local_account?.unreadeds,
			cur_account?.unreadeds
		);
		unreadeds = rm_double(unreadeds, "postId");
		unreadeds = unreadeds.filter((a) => {
			if (readeds.some((r) => r.postId === a.postId)) return false;
			if (typeId && a.postTypeId !== typeId) return false;
			return true;
		});
		return unreadeds;
	};

	const getReadedsPosts = () => {
		let cur_account = getCurAccount();
		return cur_account.readeds ? cur_account.readeds : [];
	};

	/**
	 * Set a post as readed using postId
	 * @param {PostModel} post { postId: 1654, postTypeId: 1 }
	 * @return {boolean}
	 * @example
	 * const { setAsReaded } = useAccount();
	 *
	 * setAsReaded(<postId>);
	 */
	const setAsReaded = (post) => {
		let unreadeds = getUnreadedPosts();
		let local_account = getLocalAccount();
		let readeds = getReadedsPosts();

		if (!post) return false;
		const idx = unreadeds.findIndex(
			(u) => u.postId === post.postId
		);
		if (idx !== -1) {
			unreadeds.splice(idx, 1);
			local_account.unreadeds = unreadeds;
		}
		readeds.push(post);
		local_account.readeds = rm_double(readeds, "postId");
		saveInLocal(local_account);
		setCurAccount(local_account);
		queryClient.invalidateQueries(["FetchLastsPosts"]);
		return true;
	};

	/**
	 * Check if the post with the provided postId has been readed
	 * @param {int} postId
	 * @param {boolean} only_cur_sess check only unreaded new posts from current session
	 * @return {boolean}
	 * @example
	 * const { isReaded } = useAccount();
	 *
	 * if (isReaded(<postId>))
	 *   return ("Readed")
	 * else
	 *   return ("Post not readed")
	 * });
	 */
	const isReaded = (postId, only_cur_sess) => {
		if (only_cur_sess) {
			let readeds = getReadedsPosts();
			return !!readeds.find((a) => a.postId === postId);
		}
		let unreadeds = getUnreadedPosts();
		return !unreadeds.find((a) => a.postId === postId);
	};

	const fetchInfos = (update_if_diff) => {
		let prom = Promise.all([fetchRole(), fetchRights(), fetchMe()]).then(
			(resp) => {
				let ret = {
					role: resp[0],
					rights: resp[1],
					infos: Array.isArray(resp[2]) ? resp[2][0] : resp[2],
				};


				if (update_if_diff) checkInfosDiff(ret.infos);
				return ret;
			}
		);
		return prom;
	};

	const checkInfosDiff = (new_account) => {
		let cur = getCurAccount();

		if (cur.isTemp) return false;
		if (JSONDiff(new_account, cur, ["lastConnexion", "lastChanged"])) {
			let acc = { ...cur, ...new_account };
			setCurAccount(acc);
			saveInLocal(acc);
			// Don't reset template query — it causes infinite refetch loops
			// queryClient.resetQueries(["Template", "CurPage"]);
		}
	};

	const fetchMe = async () => genRequest("me").then((resp) => {
		const data = Array.isArray(resp) ? resp[0] : resp;
		if (!data) return data;
		// Data arrives in camelCase from the API.
		// `/me` answers `id`: alias it to `employeeId` once, here
		if (data.id !== undefined) data.employeeId = data.id;
		// `employees.login` is empty in v2: same alias as AccountObject (e-mail = login)
		data.login = data.login || data.mail;
		if (data.isActive !== undefined && data.isVisibleInApp === undefined) data.isVisibleInApp = data.isActive;
		if (data.profilePicture && !data.image) data.image = data.profilePicture;
		return data;
	});

	/**
	 * Fetch user role
	 * @return {promise} Return a string with the role when resolved
	 * @example
	 * const { fetchRole } = useAccount();
	 *
	 * fetchRole().then((role) => {
	 *   return (role)
	 * }, (e) => {
	 *   return ("Error fetching role", e);
	 * });
	 */
	const fetchRole = async () => {
		return genRequest("me").then((resp) => {
			let new_account = account;
			new_account.role = resp.role;
			return new_account.role;
		}).catch(() => {
			// Fallback: use role already on account from /Me
			return account.role || "UserPublisher";
		});
	};

	/**
	 * Fetch user rights
	 * @return {promise} Return JSON rights
	 * @example
	 * const { fetchRights } = useAccount();
	 *
	 * fetchRights().then((role) => {
	 *   return (role)
	 * }, (e) => {
	 *   return ("Error fetching rights", e);
	 * });
	 */
	const fetchRights = () => genRequest("me/rights").catch(() => []);

	/**
	 * Check if the current user is superior or equal to provided role
	 * @return {int} -1 if inferior, 0 if equal, 1 if superior
	 * @example
	 * const { account, isSup } = useAccount();
	 *
	 * if (isSup("SiteAdmin"))
	 *   return ( <div>Welcome, { account.firstName }</div> );
	 * else
	 *   return ( <div>FORBIDDEN</div> );
	 */
	const isSup = (role) => {
		let myRole = getCurAccount().role;

		if (!role) return 1;

		if (myRole === "EloAdmin") return 1;

		if (role === "UserPublisher" || role === "UserNoPublisher") return 1;

		if (myRole === role) return 0;

		if (myRole === "SiteAdmin")
			if (
				role === "AllReaderPublisherRole" ||
				role === "AllReaderNoPublisherRole" ||
				role === "SuperAdmin"
			)
				return -1;

		if (myRole === "SuperAdmin")
			if (
				role === "AllReaderPublisherRole" ||
				role === "AllReaderNoPublisherRole" ||
				role === "SuperAdmin" ||
				role === "SiteAdmin"
			)
				return 1;
		return -1;
	};

	const isAdmin = () => isSup("SiteAdmin") >= 0;

	const setSettings = (obj, value) => {
		let acc = getCurAccount();

		if (!acc.settings) acc.settings = {};
		if (typeof obj === "string") {
			acc.settings[obj] = value;
		} else {
			acc.settings = obj;
		}
		setCurAccount(acc);
		saveInLocal(acc);
	};

	const getSettings = (name) => {
		let acc = getCurAccount();
		let setts = acc.settings;
		if (!setts) return name ? false : {};
		if (name) return setts[name] ? setts[name] : false;
		return setts ? setts : false;
	};

	const resetToken = () => {
		let acc = getCurAccount();
		acc.tokenRegeneration = true;
		if (acc.token) {
			acc.token = false;
			setCurAccount(acc);
			saveInLocal(acc);
		}
		return updateInfos(acc);
	};

	function updateLastCo() {
		let new_account = getCurAccount(true);
		let date = formatDate();
		new_account.lastConnexion = date;
		if (!new_account.firstConnexion) new_account.firstConnexion = date;
		setCurAccount(new_account);
		return genRequest("me/update-last-connexion", null, "post").catch(() => {});
	}

	function updateInfos(values) {
		const mapped = {
			firstName: values.firstName,
			lastName: values.lastName,
			phone: values.telephone,
			displayPhone: values.displayPhone,
			displayMail: values.displayMail,
		};
		// Remove undefined keys
		Object.keys(mapped).forEach((k) => mapped[k] === undefined && delete mapped[k]);
		return genRequest("me", mapped, "put").catch(() => {});
	}

	/**
	 * Clear sessionStorage and reload the page
	 * @example
	 * const { account, isSup, disconnect } = useAccount();
	 *
	 * if (isSup("SiteAdmin"))
	 *   return ( <div>Welcome, { account.firstName }</div> );
	 * else { // The user is disconnected if he is not at least SiteAdmin
	 *   disconnect();
	 *   return (false);
	 * }
	 */
	const disconnect = (message, timeout) => {
		if (message) toast(message, { autoClose: timeout || 5000 });
		setTimeout(() => {
			clearSession();
			window.location.replace("/#disconnected");
		}, timeout || 0);
	};

	return useMemo(() => ({
		account: account,
		setAccount: setCurAccount,
		setNewPosts,
		getUnreadedPosts,
		setAsReaded,
		isReaded,
		unread,
		isSup,
		isAdmin,
		fetchInfos,
		fetchRole,
		fetchRights,
		disconnect,
		setSettings,
		getSettings,
		resetToken,
		updateLastCo,
	}), [account]);
}

export function getCurAccount(forceCur) {
	const sess_storage = new Storage(null, null, localStorage);
	const accountString = sess_storage.getItem("account");
	if (!accountString) return {};

	let userAccount = accountString;
	if (userAccount.tempValues && !forceCur)
		return {
			...userAccount.tempValues,
			settings: userAccount.settings,
			isTemp: true,
		};

	return userAccount;
}

export default useAccount;
